// src/utils/realData.js

import axios from 'axios';

// Configurazione della sorgente dati
// Puoi cambiare questo valore in 'strapi' quando collegherai Strapi
const DATA_SOURCE = process.env.REACT_APP_DATA_SOURCE || 'strapi'; // 'local', 'static', 'strapi', 'all'
const API_URL = process.env.REACT_APP_API_URL || 'https://cms-finday-v2.onrender.com/api'; // URL del futuro Strapi

// Cache per i dati
let eventsCache = null;
let categoriesCache = null;

// Categorie definite con gli ID corretti
const baseCategories = [
  { "id": 1, "name": "Musica", "color": "red" },
  { "id": 3, "name": "Cinema", "color": "blue" },
  { "id": 4, "name": "Danza/Balletto", "color": "purple" },
  { "id": 5, "name": "Gastronomia", "color": "brown" },
  { "id": 6, "name": "Festa/Tradizione", "color": "orange" },
  { "id": 7, "name": "Teatro", "color": "teal" },
  { "id": 8, "name": "Eventi Religiosi", "color": "indigo" },
  { "id": 9, "name": "Cultura/Conferenze", "color": "green" },
  { "id": 10, "name": "Workshop/Formazione", "color": "yellow" },
  { "id": 11, "name": "Sport", "color": "lime" }
];



// --- PROVIDER PER DATI LOCALI ---
const getLocalEvents = async () => {
  try {
    // Importa i dati dal tuo file JSON locale
    const localData = await import('../data/events.json');
    
    // Mappa i dati dal formato JSON al formato che userai nell'app
    return localData.default.map(event => ({
      id: event.id,
      title: event.title,
      description: event.description,
      date: new Date(event.startDate),
      endDate: event.endDate ? new Date(event.endDate) : null,
      latitude: event.latitude,
      longitude: event.longitude,
      location: event.location,
      city: event.city,
      price: event.ticketPrice,
      categories: mapCategoriesToLocal(event.category_ids),
      image: event.image_path,
      organizer: event.organizers,
      website: event.url,
      contact: event.email,
      capacity: event.capacity || 0
    }));
  } catch (error) {
    console.error("Errore nel caricamento degli eventi dal file locale:", error);
    return [];
  }
};

const getLocalCategories = async () => {
  return baseCategories;
};

// --- PROVIDER PER STRAPI CMS ---
const getStrapiEvents = async () => {
  try {
    const response = await axios.get(`${API_URL}/events?populate=*`);
    
    // Il formato della risposta di Strapi è diverso, quindi lo normalizziamo
    return response.data.data.map(item => {
      const event = item.attributes;
      return {
        id: event.id,
        title: event.title,
        description: event.description,
        date: new Date(event.startDate),
        endDate: event.endDate ? new Date(event.endDate) : null,
        latitude: event.latitude,
        longitude: event.longitude,
        location: event.location,
        city: event.city,
        price: event.ticketPrice,
        categories: mapCategoriesFromStrapi(event.category_ids),
        image: event.image_path,
        organizer: event.organizers,
        website: event.url,
        contact: event.email,
        capacity: event.capacity || 0
      };
    });
    } catch (error) {
    console.error("Errore nel caricamento degli eventi da Strapi:", error);
    // Fallback a dati locali se Strapi non è disponibile
    return await getLocalEvents();
  }
};

const getStrapiCategories = async () => {
  try {
    const response = await axios.get(`${API_URL}/categories`);
    
    return response.data.data.map(item => ({
      id: item.id,
      name: item.attributes.name,
      color: item.attributes.color || getDefaultColorForCategory(item.id)
    }));
  } catch (error) {
    console.error("Errore nel caricamento delle categorie da Strapi:", error);
    return baseCategories;
  }
};

// --- FUNZIONI HELPER ---

// Mappa gli ID delle categorie alle categorie complete (per dati locali)
const mapCategoriesToLocal = (categoryIds) => {
  if (!categoryIds || !Array.isArray(categoryIds)) return [];
  
  return categoryIds.map(id => {
    const category = baseCategories.find(cat => cat.id === id);
    return category || null;
  }).filter(category => category !== null);
};

// Mappa le categorie da Strapi al formato dell'app
const mapCategoriesFromStrapi = (categoriesData) => {
  if (!categoriesData || !Array.isArray(categoriesData)) return [];
  
  return categoriesData.map(catData => ({
    id: catData.id,
    name: catData.attributes.name,
    color: catData.attributes.color || getDefaultColorForCategory(catData.id)
  }));
};

// Funzione per ottenere un colore predefinito per una categoria in base all'ID
const getDefaultColorForCategory = (categoryId) => {
  const category = baseCategories.find(cat => cat.id === categoryId);
  return category ? category.color : "gray";
};

// --- FUNZIONI PRINCIPALI ESPORTATE ---

// Funzione per ottenere tutti gli eventi
export const getRealEvents = async () => {
  // Se abbiamo già caricato gli eventi, li restituiamo dalla cache
  if (eventsCache) return eventsCache;
  
  let events;
  
  // Scegli la fonte dati in base alla configurazione
  switch (DATA_SOURCE) {
    case 'strapi':
      events = await getStrapiEvents();
      break;
    case 'local':
      events = await getLocalEvents();
      break;
    default:
      events = [];
  }
  
  // Salva in cache
  eventsCache = events;
  return events;
};

// Funzione per ottenere tutte le categorie
export const getRealCategories = async () => {
  // Se abbiamo già caricato le categorie, le restituiamo dalla cache
  if (categoriesCache) return categoriesCache;
  
  let categories;
  
  // Scegli la fonte dati in base alla configurazione
  switch (DATA_SOURCE) {
    case 'strapi':
      categories = await getStrapiCategories();
      break;
    case 'local':
      categories = await getLocalCategories();
      break;
    default:
      categories = baseCategories;
  }
  
  // Salva in cache
  categoriesCache = categories;
  return categories;
};

// Funzione per ottenere un evento specifico per ID
export const getEventById = async (id) => {
  const events = await getRealEvents();
  return events.find(event => event.id === parseInt(id));
};

// Funzione per ottenere eventi filtrati per categoria
export const getEventsByCategory = async (categoryId) => {
  const events = await getRealEvents();
  return events.filter(event => 
    event.categories.some(cat => cat.id === parseInt(categoryId))
  );
};

// Funzione per ottenere eventi filtrati per città
export const getEventsByCity = async (city) => {
  const events = await getRealEvents();
  return events.filter(event => 
    event.city.toLowerCase() === city.toLowerCase()
  );
};

// Funzione per ricerca di eventi per testo
export const searchEvents = async (searchTerm) => {
  const events = await getRealEvents();
  const term = searchTerm.toLowerCase();
  
  return events.filter(event => 
    event.title.toLowerCase().includes(term) || 
    event.description.toLowerCase().includes(term) || 
    event.city.toLowerCase().includes(term) ||
    event.location.toLowerCase().includes(term)
  );
};

// Funzione per ottenere eventi futuri
export const getUpcomingEvents = async () => {
  const events = await getRealEvents();
  const now = new Date();
  
  return events.filter(event => {
    // Se l'evento ha una data di fine e quella è nel futuro, includi l'evento
    if (event.endDate && new Date(event.endDate) >= now) {
      return true;
    }
    // Altrimenti controlla solo la data di inizio
    return new Date(event.date) >= now;
  }).sort((a, b) => new Date(a.date) - new Date(b.date));
};

// Funzione per ottenere eventi in corso
export const getOngoingEvents = async () => {
  const events = await getRealEvents();
  const now = new Date();
  
  return events.filter(event => {
    const startDate = new Date(event.date);
    const endDate = event.endDate ? new Date(event.endDate) : startDate;
    
    // L'evento è in corso se la data attuale è tra la data di inizio e la data di fine
    return startDate <= now && now <= endDate;
  });
};

// Funzione per cancellare la cache (utile quando si aggiornano i dati)
export const clearCache = () => {
  eventsCache = null;
  categoriesCache = null;
};

// Funzione per aggiornare la cache con nuovi dati (utile per testing o aggiornamenti manuali)
export const updateCache = (events, categories) => {
  if (events) eventsCache = events;
  if (categories) categoriesCache = categories;
};

// Funzione per ottenere eventi nelle vicinanze di una posizione
export const getNearbyEvents = async (lat, lng, radiusKm = 20) => {
  const events = await getRealEvents();
  
  // Calcola la distanza usando la formula di Haversine
  return events.filter(event => {
    const distance = calculateDistance(
      lat, lng, 
      event.latitude, event.longitude
    );
    return distance <= radiusKm;
  }).sort((a, b) => {
    const distanceA = calculateDistance(lat, lng, a.latitude, a.longitude);
    const distanceB = calculateDistance(lat, lng, b.latitude, b.longitude);
    return distanceA - distanceB;
  });
};

// Calcola la distanza in km tra due punti geografici usando la formula di Haversine
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Raggio della Terra in km
  const dLat = degToRad(lat2 - lat1);
  const dLon = degToRad(lon2 - lon1);
  
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(degToRad(lat1)) * Math.cos(degToRad(lat2)) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  
  return distance;
};

// Converte gradi in radianti
const degToRad = (deg) => {
  return deg * (Math.PI/180);
};

// Esporta anche DATA_SOURCE per permettere al resto dell'applicazione
// di sapere quale fonte dati sta utilizzando
export const getDataSource = () => DATA_SOURCE;
